/* eslint-disable no-fallthrough */
import { VerifySceneEnum } from '@/shared/verify-credit-controller/types';
import { UserInfoStatus } from '@/enums/UserInfoStatus';
import { UserInfoProgress } from '@/api/homeV2Status/types';
import { VerifyStageEnum } from './types';
import { transformServerStepCountToStageEnumList } from './utils';
import { TP_TWO_FACTOR_EXPOSE, TP_TWO_FACTOR_SUBMIT } from './tianqi';

interface VerifyStageUbtConfig {
  pageId?: string;
  buttonName?: string;
  exposeName?: string;
}

export const VERIFY_STAGE_UBT_CONFIG_MAPS:
  Partial<Record<VerifyStageEnum, VerifyStageUbtConfig>> = {
    [VerifyStageEnum.USER_BASE]: {
      buttonName: 'finance_personalInformation_nextStep',
      exposeName: 'finance_personalInformation_expose',
    },
    [VerifyStageEnum.CONTACT_INFO]: {
      buttonName: 'finance_contactInformation_nextStep',
      exposeName: 'finance_contactInformation_expose',
    },
    [VerifyStageEnum.TWO_ELEMENTS]: {
      pageId: '臻有钱_身份认证',
      buttonName: TP_TWO_FACTOR_SUBMIT,
      exposeName: TP_TWO_FACTOR_EXPOSE,
    },
  };

/** 默认页面ID */
export const DEFAULT_PAGE_ID = 'finance_personalInformation';

// eslint-disable-next-line max-len
export const getStageUbtConfig = (name: VerifyStageEnum): VerifyStageUbtConfig => VERIFY_STAGE_UBT_CONFIG_MAPS[name] || {};

/** 是否为 动态配置的 补充资料的场景 */
export const isConfigSupplyPersonalScene = (scene: VerifySceneEnum): boolean => [
  VerifySceneEnum.CREDIT_SUPPLY_PERSONAL,
  VerifySceneEnum.LOAN_SUPPLY_PERSONAL,
].includes(scene);

/** 是否为 前端定制化场景 */
export const getCurrentIsCustomScene = (scene: VerifySceneEnum): boolean => [
  VerifySceneEnum.CREDIT_SUPPLY_PERSONAL,
  VerifySceneEnum.LOAN_SUPPLY_PERSONAL,
  VerifySceneEnum.PERSONAL_PROFILE_MODIFY,
  VerifySceneEnum.PERSONAL_CONTACT_MODIFY,
  VerifySceneEnum.PERSONAL_IDENTITY_MODIFY,
  VerifySceneEnum.MARKET_PRE_SUPPLEMENT,
  VerifySceneEnum.USER_BASE_CONTACT_SUPPLEMENT,
  VerifySceneEnum.PERSONAL_NAME_MODIFY,
  VerifySceneEnum.CREDIT_WITH_INVALID_IDENTITY,
  VerifySceneEnum.UPDATE_EXPIRES_IDENTITY,
].includes(scene);

/** 该场景是否需要依赖服务的用户资料信息(f.loan.panel.queryUserWriteProgress数据) */
export const isDependUserInfoProgressScene = (scene: VerifySceneEnum): boolean => [
  /** 通过判断fillInTheInformation是否为”3“Completed，进行检验步骤设置 */
  VerifySceneEnum.USER_BASE_CONTACT_SUPPLEMENT,
  VerifySceneEnum.CREDIT_WITH_INVALID_IDENTITY,
  /** 通过判断identityExpiresFor30字段身份证是否即将过期，展示通知栏提醒 */
  VerifySceneEnum.CREDIT_PRE_VERIFY,
  VerifySceneEnum.CREDIT_PRE_VERIFY_OCR_AFTER,
  VerifySceneEnum.LOAN_UNION_SUPPLEMENT,
  VerifySceneEnum.UPDATE_EXPIRES_IDENTITY,
  VerifySceneEnum.PERSONAL_IDENTITY_MODIFY,
].includes(scene);

/** 是否需要在提交资料之前，预检用户信息：身份证实名一致性、业务协议是否已授权同意 */
export const isNeedSubmitVerifyPreCheckScene = (scene: VerifySceneEnum): boolean => [
  VerifySceneEnum.PERSONAL_PROFILE_MODIFY,
  VerifySceneEnum.PERSONAL_CONTACT_MODIFY,
  VerifySceneEnum.PERSONAL_IDENTITY_MODIFY,
  VerifySceneEnum.MARKET_PRE_SUPPLEMENT,
  VerifySceneEnum.CREDIT_MIX_SUPPLEMENT,
  VerifySceneEnum.CREDIT_WITH_INVALID_IDENTITY,
  VerifySceneEnum.CREDIT_PRE_VERIFY,
  VerifySceneEnum.CREDIT_PRE_VERIFY_OCR_AFTER,
  VerifySceneEnum.CREDIT_MIDDLE_ONLY_OCR,
].includes(scene);

/** 是否需要检查身份证清晰分 */
export const isNeedCheckIdentityClarityScore = (scene: VerifySceneEnum): boolean => [
  VerifySceneEnum.CREDIT_WITH_INVALID_IDENTITY,
].includes(scene);

/**
 * 前端定制化场景，对应资料需要填写的步骤在此配置
 * 若”活体验证“不在最后一步，则 “h5、微信小程序”场景下，因location.href回跳，可能导致进度(浏览器内存)丢失
 */
export function getCustomSceneVerifyStageConfig(
  scene: VerifySceneEnum,
  queryParams?: Record<string, unknown>,
  userInfoProgress?: UserInfoProgress | null,
): VerifyStageEnum[] {
  switch (scene) {
    /** 前端定制场景，授信流程，补充授信“个人信息”的后台配置的字段 */
    case VerifySceneEnum.CREDIT_SUPPLY_PERSONAL:
      /** 前端定制场景，提现流程，补充提现“个人信息”的后台配置的字段 */
    case VerifySceneEnum.LOAN_SUPPLY_PERSONAL:
    /** 前端定制场景，短信营销场景，只填写“个人信息”页面 */
    case VerifySceneEnum.MARKET_PRE_SUPPLEMENT:
      return [VerifyStageEnum.USER_BASE];

    /** 以下场景，前端定制化，即为编辑的场景 */
    case VerifySceneEnum.PERSONAL_PROFILE_MODIFY:
      return [VerifyStageEnum.USER_BASE];
    case VerifySceneEnum.PERSONAL_CONTACT_MODIFY:
      return [VerifyStageEnum.CONTACT_INFO];
    /** 身份证更新，动态决策是否需要活体认证 */
    case VerifySceneEnum.PERSONAL_IDENTITY_MODIFY:
      return [VerifyStageEnum.ID_CARD, VerifyStageEnum.BIO_AUTH];
    case VerifySceneEnum.UPDATE_EXPIRES_IDENTITY:
      return [VerifyStageEnum.ID_CARD, VerifyStageEnum.BIO_AUTH];
    case VerifySceneEnum.USER_BASE_CONTACT_SUPPLEMENT: {
      const { needEducation, needMonthSalary, needEmergencyContact } = queryParams || {};
      const stages = [];
      if (needEducation || needMonthSalary) {
        stages.push(VerifyStageEnum.USER_BASE);
      }

      if (needEmergencyContact) {
        stages.push(VerifyStageEnum.CONTACT_INFO);
      }

      /** 若用户资料未填写完，则除了补充上述指定信息，还需加上身份证和活体验证的流程 */
      if (userInfoProgress && userInfoProgress.fillInTheInformation !== UserInfoStatus.Completed) {
        stages.push(VerifyStageEnum.ID_CARD);
        stages.push(VerifyStageEnum.BIO_AUTH);
      }

      return stages;
    }
    case VerifySceneEnum.CREDIT_WITH_INVALID_IDENTITY: {
      /** 若用户资料流程未完成，则取服务端的stepCount，计算检验步骤 */
      if (userInfoProgress && userInfoProgress.fillInTheInformation !== UserInfoStatus.Completed) {
        return transformServerStepCountToStageEnumList(userInfoProgress.stepCount, true);
      }
      return [VerifyStageEnum.ID_CARD, VerifyStageEnum.BIO_AUTH];
    }
    case VerifySceneEnum.PERSONAL_NAME_MODIFY:
      return [VerifyStageEnum.CONTACT_INFO];
    default:
      return [];
  }
}

/** 前端定制化场景，对应资料组件的样式定制配置 */
export const customSceneUIControls = {
  /** 默认都展示(最高可借 + tip文案)，若只展示tip文案“哈啰承诺信息安全加密，严格保障你的信息安全”，配置返回为“true" */
  getIsSingleHeaderSecurity: (scene: VerifySceneEnum): boolean => {
    switch (scene) {
      case VerifySceneEnum.PERSONAL_PROFILE_MODIFY:
      case VerifySceneEnum.PERSONAL_CONTACT_MODIFY:
      case VerifySceneEnum.PERSONAL_IDENTITY_MODIFY:
      case VerifySceneEnum.MARKET_PRE_SUPPLEMENT:
      case VerifySceneEnum.PERSONAL_NAME_MODIFY:
      case VerifySceneEnum.USER_BASE_CONTACT_SUPPLEMENT:
        return true;
      default:
        return false;
    }
  },
  /** 默认使用沉浸式导航栏，不使用请在此配置返回为“false" */
  getIsUseImmerseNarbar: (scene: VerifySceneEnum): boolean => {
    switch (scene) {
      case VerifySceneEnum.PERSONAL_PROFILE_MODIFY:
      case VerifySceneEnum.PERSONAL_CONTACT_MODIFY:
      case VerifySceneEnum.PERSONAL_IDENTITY_MODIFY:
      case VerifySceneEnum.PERSONAL_NAME_MODIFY:
      case VerifySceneEnum.USER_BASE_CONTACT_SUPPLEMENT:
      case VerifySceneEnum.UPDATE_EXPIRES_IDENTITY:
        return false;
      default:
        return true;
    }
  },
};
